import React, { ReactNode } from 'react'
import { Box } from '@mui/material'

import { BLUE_200, BLUE_300, BLUE_600, GRAY_100, GRAY_400, GRAY_500, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'

/** @interface Props for MUIRadioCard */
export interface Props {
  /** Radio group the card belongs to. */
  name: string
  /** Value the card stands for in its group. */
  value: string
  /** Whether this card is the group's current choice. */
  checked: boolean
  /** Text on the card. */
  label: string
  /** Called with the card's value when it is chosen. */
  onChange: (value: string) => void
  /** `sm` is 38px tall, to line up with a small field; `md` is 40px. */
  size?: 'sm' | 'md'
  /** `hug` sizes the card to its label, for a row of cards; `fill` takes its container's width. */
  width?: 'hug' | 'fill'
  disabled?: boolean
  /** Leading icon, before the label */
  icon?: ReactNode
  /** Independent toggles: a checkbox under the card, so several cards can be on at once. `onChange(value)` fires on every toggle */
  isMultiple?: boolean
  /** Id of the element that describes the choice, forwarded to the native input */
  'aria-describedby'?: string
  'data-cy'?: string
}

/**
 * @component A selectable card for one choice among several, the design system's RadioButton card: a real
 * radio input under the card, so a group of them is reachable with Tab and moved through with the arrow keys.
 * With `isMultiple` the input is a checkbox and each card toggles on its own.
 * @example
 * <MUIRadioCard name="category" value="photography" label="Photography" checked={active === 'photography'} onChange={setActive} size="sm" width="hug" />
 */
export const MUIRadioCard: React.FC<Props> = ({
  name,
  value,
  checked,
  label,
  onChange,
  size = 'md',
  width = 'fill',
  disabled = false,
  icon,
  isMultiple = false,
  'aria-describedby': ariaDescribedBy,
  'data-cy': dataCy,
}) => (
  <Box
    component="label"
    data-cy={dataCy}
    sx={{
      position: 'relative',
      display: 'inline-flex',
      flexShrink: width === 'hug' ? 0 : undefined,
      width: width === 'hug' ? 'auto' : '100%',
      cursor: disabled ? 'not-allowed' : 'pointer',
      ['&:hover input:not(:disabled) ~ .radiocard-card']: { borderColor: BLUE_600 },
    }}
  >
    <Box
      component="input"
      type={isMultiple ? 'checkbox' : 'radio'}
      name={name}
      value={value}
      checked={checked}
      disabled={disabled}
      aria-describedby={ariaDescribedBy}
      onChange={() => onChange(value)}
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        m: 0,
        opacity: 0,
        cursor: 'inherit',
        ['&:focus-visible ~ .radiocard-card']: { boxShadow: `0 0 0 2px ${BLUE_300}` },
      }}
    />
    <Box
      className="radiocard-card"
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        py: size === 'sm' ? '0.6rem' : '0.8rem',
        px: '1.2rem',
        backgroundColor: disabled ? GRAY_100 : checked ? BLUE_200 : WHITE,
        border: `1px solid ${checked && !disabled ? BLUE_600 : GRAY_400}`,
        borderRadius: '0.8rem',
        transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
      }}
    >
      {!!icon &&
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            flexShrink: 0,
            marginRight: '.8rem',
            '& svg': { fontSize: '2rem', width: '1em', height: '1em' },
          }}
        >
          {icon}
        </Box>
      }
      <Box
        component="span"
        sx={{
          flex: width === 'hug' ? '0 1 auto' : '1 0 0',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '1.6rem',
          lineHeight: '2.4rem',
          fontWeight: checked && !disabled ? 450 : 350,
          color: disabled ? GRAY_500 : checked ? GRAY_900 : GRAY_700,
        }}
      >
        {label}
      </Box>
    </Box>
  </Box>
)
